import React, { type JSX, createContext, useContext, useState } from 'react';
import Snackbar, { SnackbarOrigin } from '@mui/material/Snackbar';
import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

type Props = {
  yPos: number;
  children: React.ReactNode;
};

type State = {
  open: boolean;
  content?: React.ReactNode;
  key: number;
  autoHideDuration?: number;
} & SnackbarOrigin;

type SnackbarType = {
  showSnackbar: (props: Partial<State>) => void;
  closeSnackbar: (event: any, reason: any, key: any) => void;
};

const DEFAULT_AUTO_HIDE_DURATION = 3000;

/**
 * Monotonic key source. A timestamp collides when two snackbars are raised inside the
 * same millisecond, which hands React duplicate keys and makes the close handler drop
 * both messages at once.
 */
let lastSnackbarKey = 0;

const nextSnackbarKey = (): number => ++lastSnackbarKey;

const SnackbarContext = createContext<SnackbarType>({} as SnackbarType);
const useSnackbar = (): SnackbarType => useContext(SnackbarContext);

const SnackbarProvider = ({ yPos, children }: Props): JSX.Element => {
  const [messages, setMessage] = useState<State[]>([]);

  const handleOpenSnackbar = (props: Partial<State>): void => {
    const key = nextSnackbarKey();
    setMessage((prev) => [
      ...prev,
      {
        open: true,
        vertical: 'bottom',
        horizontal: 'left',
        ...props,
        key,
      },
    ]);
  };

  const handleCloseSnackbar =
    (key: number) =>
    (_e: Event | React.SyntheticEvent<any, Event>, reason: string): void => {
      if (reason === 'clickaway') return;
      setMessage((prev) => prev.filter((snack) => snack.key !== key));
    };

  const value = {
    showSnackbar: handleOpenSnackbar,
    closeSnackbar: handleCloseSnackbar,
  };

  return (
    <SnackbarContext.Provider value={value}>
      {messages.map((msg, index) => (
        <Snackbar
          style={{ bottom: index * 60 + 20, left: yPos }}
          key={msg.key}
          open={msg.open}
          autoHideDuration={msg.autoHideDuration ?? DEFAULT_AUTO_HIDE_DURATION}
          onClose={handleCloseSnackbar(msg.key)}
          message={msg.content}
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={(e): void => handleCloseSnackbar(msg.key)(e, 'click')}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        />
      ))}
      {children}
    </SnackbarContext.Provider>
  );
};

export { SnackbarProvider, useSnackbar };
